import axios from "axios";
import requests from "./httpServices.js";

const PRESIGNED_THRESHOLD = 95 * 1024 * 1024; // 95MB — use presigned URL above this

class UploadService {
  /**
   * Upload a file directly (without folder context)
   * Uses presigned S3 URL for files > 95MB to bypass Cloudflare limit
   */
  async uploadFile(file, onProgress) {
    if (file.size > PRESIGNED_THRESHOLD) {
      return this.uploadFilePresigned(file, onProgress);
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await requests.post(`/upload/single`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });

      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload large files directly to S3 via multipart presigned URLs (bypasses Cloudflare 100MB limit)
   * Splits file into 10MB chunks and uploads 4 in parallel for speed
   */
  async uploadFilePresigned(file, onProgress) {
    const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB per part (fewer parts = less overhead)
    const CONCURRENCY = 6; // parallel uploads
    const totalParts = Math.ceil(file.size / CHUNK_SIZE);

    let uploadId, key, fileUrl;

    try {
      // Step 1: Initiate multipart upload and get presigned URLs for all parts
      const initData = await requests.post(`/upload/multipart/initiate`, {
        fileName: file.name,
        mimeType: file.type,
        folder: 'files',
        totalParts,
      });

      if (!initData.success) {
        throw new Error(initData.message || 'Failed to initiate upload');
      }

      uploadId = initData.uploadId;
      key = initData.key;
      fileUrl = initData.fileUrl;

      // Step 2: Upload chunks in parallel with concurrency limit
      const partResults = [];
      const partProgress = new Array(totalParts).fill(0);

      const uploadPart = async ({ partNumber, url }) => {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);

        const response = await axios.put(url, blob, {
          headers: { 'Content-Type': file.type },
          timeout: 300000, // 5 min per chunk
          onUploadProgress: (evt) => {
            if (evt.total) {
              partProgress[partNumber - 1] = evt.loaded;
              if (onProgress) {
                const totalLoaded = partProgress.reduce((a, b) => a + b, 0);
                onProgress(Math.round((totalLoaded * 100) / file.size));
              }
            }
          },
        });

        const etag = response.headers?.etag || response.headers?.ETag;
        partResults.push({ partNumber, etag });
      };

      // Process parts with concurrency limit
      const parts = [...initData.parts];
      for (let i = 0; i < parts.length; i += CONCURRENCY) {
        const batch = parts.slice(i, i + CONCURRENCY);
        await Promise.all(batch.map(uploadPart));
      }

      // Step 3: Complete multipart upload
      partResults.sort((a, b) => a.partNumber - b.partNumber);
      const completeData = await requests.post(`/upload/multipart/complete`, {
        key,
        uploadId,
        parts: partResults,
      });

      if (!completeData.success) {
        throw new Error(completeData.message || 'Failed to finalize upload');
      }

      return { success: true, url: completeData.fileUrl };
    } catch (error) {
      // Abort multipart upload on failure to clean up orphaned parts
      if (uploadId && key) {
        requests.post(`/upload/multipart/abort`, { key, uploadId }).catch(() => {});
      }
      throw this.handleError(error);
    }
  }

  /**
   * Upload an image file
   * @param file - Image file object
   * @param onProgress - Progress callback
   * @returns Promise<{ url: string }>
   */
  async uploadImage(file, onProgress) {
    // Validate it's an image
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    return this.uploadFile(file, onProgress);
  }

  /**
   * Handle API errors
   * @param error - Error object
   * @returns Error with formatted message
   */
  handleError(error) {
    if (error.response?.data) {
      const { message, errors } = error.response.data;

      if (errors && typeof errors === "object") {
        const errorMessages = Object.values(errors).flat();
        return new Error(errorMessages.join(", "));
      }

      if (message) {
        return new Error(message);
      }
    }

    if (error.message) {
      return new Error(error.message);
    }

    return new Error("An unexpected error occurred");
  }
}

// Export singleton instance
const uploadService = new UploadService();
export default uploadService;
